import React, { useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Flex,
  Heading,
  Image,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Link as ChakraLink } from '@chakra-ui/react';
import { IUploadFile } from '../../interfaces';
import folderIcon from '/assets/images/icons/folder.svg';
import { useTranslation } from 'react-i18next';

interface DocumentUploadProps {
  onFilesAdded: (files: IUploadFile[]) => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ onFilesAdded }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const toast = useToast();
  const supportedFormats = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
  const MAX_FILE_SIZE_MB: string = '50';

  /**
   * Opens the file browser dialog.
   */
  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * Handles file selection via file input change event.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The file input change event.
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    handleFiles(files);
  };

  /**
   * Handles the drag over event to indicate dragging state.
   * @param {React.DragEvent<HTMLDivElement>} event - The drag over event.
   */
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  /**
   * Handles the drag leave event to reset dragging state.
   * @param {React.DragEvent<HTMLDivElement>} event - The drag leave event.
   */
  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  /**
   * Handles the drop event to process dropped files.
   * @param {React.DragEvent<HTMLDivElement>} event - The drop event.
   */
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const files = Array.from(event.dataTransfer.files);
    handleFiles(files);
  };

  /**
   * Processes and validates the selected or dropped files.
   * @param {File[]} files - The list of selected or dropped files.
   */
  const handleFiles = (files: File[]) => {
    const validFiles: IUploadFile[] = files
      .filter((file) => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (extension && supportedFormats.includes(extension)) {
          if (file.size > MAX_FILE_SIZE) {
            toast({
              title: t('fileTooLarge'),
              description: t('fileSizeLimit').replace(
                '{{size}}',
                MAX_FILE_SIZE_MB
              ),
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
            return false;
          }
          return true;
        } else {
          toast({
            title: t('unsupportedFileFormat'),
            description: t('supportedDocumentFormats').replace(
              '{{list}}',
              supportedFormats.join(', ')
            ),
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return false;
        }
      })
      .map((file) => ({
        id: uuidv4(),
        name: file.name,
        date: '',
        time: '',
        isUploading: true,
        progress: 0,
        fileObject: file,
      }));

    if (validFiles.length) {
      onFilesAdded(validFiles);
    }
  };

  const borderColorUploadArea = useColorModeValue('alea.300', 'gray.500');
  const bgColorUploadArea = isDragging ? '#D0F4FF80' : '#D0F4FF33';

  return (
    <Box>
      <Heading size="md" fontWeight={500} mb={6}>
        {t('documentUpload')}
      </Heading>
      <Flex
        borderWidth="2px"
        borderRadius="20px"
        borderColor={borderColorUploadArea}
        borderStyle="dashed"
        p={4}
        textAlign="center"
        bg={bgColorUploadArea}
        color="alea.400"
        h={275}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        mb={4}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Image src={folderIcon} mb={10} />
        <Text color="black" fontSize="20px">
          {t('addDocuments')}{' '}
          <ChakraLink
            as="button"
            display="block"
            margin="auto"
            textDecoration="underline"
            color="alea.500"
            onClick={handleBrowseClick}
          >
            {t('browseComputer')}
          </ChakraLink>
        </Text>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          multiple
        />
      </Flex>

      <Flex justifyContent="space-between">
        <Text color="#C6C6C6" fontSize="18px">
          {t('supportedFormats')}: pdf, doc, docx, xls, xlsx
        </Text>

        <Text color="#C6C6C6" fontSize="18px">
          {t('maximumSize')}: 50 MB
        </Text>
      </Flex>
    </Box>
  );
};

export default DocumentUpload;
